// SCR-STAFF-805 (AP-CMP-02؛ US-CMP-050 تا 052؛ AC-CMP-050.1، 051.1..2، 052.1): تسویه — صورت‌وضعیت‌های قابل پرداخت (تأییدشده، پذیرفته‌شده، پرداخت جزئی؛
// قلم معترض خارج از مبلغ قابل پرداخت)، پرداخت کامل یا جزئی، دسته پرداخت بانکی با فایل CSV شبا و تکمیل با شماره پیگیری، پیش‌پرداخت با سقف
// (cmp.advance_max_percent) و تأیید مالک برای مبالغ بالای آستانه (PendingApproval؛ C-AGENT-1381).
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type CmpAdvanceView,
  type CmpPayoutBatchView,
  type CmpPayoutView,
  type CmpSettlementMethod,
  type CmpStatementSummaryView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { saveBlob } from '../billing/common';
import { WriteNotice } from '../settings/common';
import { compensationKeys, ProviderPicker, SETTLEMENT_METHODS, statementTone } from './common';
import { settlementScopes } from './StatementsPage';

export const SCR_STAFF_805 = 'SCR-STAFF-805';

type PayoutsTab = 'payable' | 'payouts' | 'advances' | 'batches';

const PAYABLE_STATUSES = new Set(['Approved', 'Acknowledged', 'PartiallyPaid', 'Disputed']);

const payoutTone = (status: string): StatusTone =>
  status === 'Completed' || status === 'Paid' || status === 'Settled' ? 'active' : 'pending';

export function PayoutsPage() {
  const { t } = useTranslation('cmp');
  const access = useAccess();
  const write = useWriteAccess('cmp.payout.manage');
  const [tab, setTab] = useState<PayoutsTab>('payable');
  const canManage = access.can('cmp.payout.manage') && write.canWrite;

  return (
    <ListPage
      screenId={SCR_STAFF_805}
      titleKey="cmp:payouts.title"
      subtitleKey="cmp:payouts.subtitle"
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <Tabs
            value={tab}
            onChange={(_, value: PayoutsTab) => setTab(value)}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab value="payable" label={t('cmp:payouts.tab_payable')} data-testid="payouts-tab-payable" />
            <Tab value="payouts" label={t('cmp:payouts.tab_payouts')} data-testid="payouts-tab-payouts" />
            <Tab value="advances" label={t('cmp:payouts.tab_advances')} data-testid="payouts-tab-advances" />
            <Tab value="batches" label={t('cmp:payouts.tab_batches')} data-testid="payouts-tab-batches" />
          </Tabs>
        </Stack>
      }
    >
      {tab === 'payable' && <PayableTable canManage={canManage} />}
      {tab === 'payouts' && <PayoutsTable canManage={canManage} />}
      {tab === 'advances' && <AdvancesTable canManage={canManage} />}
      {tab === 'batches' && <BatchesTable canManage={canManage} />}
    </ListPage>
  );
}

function useInvalidateSettlement() {
  const { session } = useTenantSession();
  const queryClient = useQueryClient();
  return () => {
    for (const key of settlementScopes(session?.tenantId)) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };
}

function PayableTable({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const invalidate = useInvalidateSettlement();
  const idempotency = useIdempotencyKey();
  const [selected, setSelected] = useState<string[]>([]);
  const [paying, setPaying] = useState<CmpStatementSummaryView | null>(null);
  const statements = useQuery({
    queryKey: [...compensationKeys.statements(session?.tenantId), 'payable'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/compensation/statements', { params: { query: {} } })),
  });
  const rows = statements.data?.items.filter(
    (row) => PAYABLE_STATUSES.has(row.status) && Number(row.payable.amount) > 0,
  );
  const batch = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/payout-batches', {
          body: { statementIds: selected, businessDate: null },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setSelected([]);
      invalidate();
    },
  });
  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );

  const columns: DataTableColumn<CmpStatementSummaryView>[] = [
    ...(canManage
      ? [
          {
            id: 'select',
            headerKey: 'cmp:statements.columns.select',
            cell: (row: CmpStatementSummaryView) => (
              <Checkbox
                size="small"
                checked={selected.includes(row.id)}
                onChange={() => toggle(row.id)}
                slotProps={{
                  input: {
                    'aria-label': t('cmp:payouts.select_statement', { name: row.beneficiaryName ?? '' }),
                  },
                }}
              />
            ),
          } satisfies DataTableColumn<CmpStatementSummaryView>,
        ]
      : []),
    {
      id: 'beneficiary',
      headerKey: 'cmp:statements.columns.beneficiary',
      cell: (row) => row.beneficiaryName ?? '—',
    },
    {
      id: 'period',
      headerKey: 'cmp:statements.period',
      cell: (row) => (
        <>
          <JalaliDate value={row.periodFrom} /> – <JalaliDate value={row.periodTo} />
        </>
      ),
    },
    {
      id: 'status',
      headerKey: 'cmp:ledger.columns.status',
      cell: (row) => (
        <StatusChip tone={statementTone(row.status)} labelKey={`cmp:statement_status.${row.status}`} />
      ),
    },
    {
      id: 'net',
      headerKey: 'cmp:statements.net',
      cell: (row) => <Money amountMinor={Number(row.net.amount)} />,
      align: 'right',
    },
    {
      id: 'paid',
      headerKey: 'cmp:statements.paid',
      cell: (row) => <Money amountMinor={Number(row.paid.amount)} />,
      align: 'right',
    },
    {
      id: 'payable',
      headerKey: 'cmp:statements.payable',
      cell: (row) => (
        <span data-testid={`payable-amount-${row.id}`}>
          <Money amountMinor={Number(row.payable.amount)} />
        </span>
      ),
      align: 'right',
    },
    {
      id: 'actions',
      headerKey: 'cmp:ledger.columns.actions',
      cell: (row) =>
        canManage ? (
          <Button size="small" onClick={() => setPaying(row)} data-testid={`payable-pay-${row.id}`}>
            {t('cmp:payouts.pay')}
          </Button>
        ) : null,
    },
  ];

  return (
    <Stack spacing={2}>
      {canManage && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            disabled={selected.length === 0 || batch.isPending}
            onClick={() => batch.mutate()}
            data-testid="payouts-create-batch"
          >
            {t('cmp:payouts.create_batch', { value: faDigits(selected.length) })}
          </Button>
          {batch.data && (
            <Typography variant="body2" data-testid="payouts-batch-created">
              {t('cmp:payouts.batch_created', { number: batch.data.number })}
            </Typography>
          )}
        </Stack>
      )}
      {batch.error ? <ErrorState error={batch.error} compact /> : null}
      <DataTable
        captionKey="cmp:payouts.payable_caption"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        isLoading={statements.isPending}
        error={statements.error}
        onRetry={() => void statements.refetch()}
        emptyTitleKey="cmp:payouts.payable_empty"
      />
      {paying && <PayDialog statement={paying} onClose={() => setPaying(null)} onDone={invalidate} />}
    </Stack>
  );
}

const paySchema = z.object({
  amount: z
    .number({ error: 'common:validation.required' })
    .int({ error: 'common:validation.money' })
    .positive({ error: 'common:validation.money' })
    .nullable(),
  method: z.string(),
  reference: z.string(),
});
type PayForm = z.input<typeof paySchema>;

/** پرداخت کامل یا جزئی یک صورت‌وضعیت (AC-CMP-051.1: مانده در وضعیت PartiallyPaid). */
function PayDialog({
  statement,
  onClose,
  onDone,
}: {
  statement: CmpStatementSummaryView;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('cmp');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<PayForm>({
    resolver: zodResolver(paySchema),
    defaultValues: { amount: Number(statement.payable.amount), method: 'BankTransfer', reference: '' },
  });
  const save = useMutation({
    mutationFn: (form: PayForm) =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/payouts', {
          body: {
            statements: [
              {
                statementId: statement.id,
                amount: form.amount ? { amount: form.amount, currency: 'IRR' } : null,
              },
            ],
            method: form.method as CmpSettlementMethod,
            reference: form.reference.trim() || null,
            businessDate: null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['amount', 'reference'],
        codeFields: { 'PHX-CMP-0207': 'amount' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-805-PAY"
      titleKey="cmp:payouts.pay_title"
      submitKey="cmp:payouts.pay"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Typography variant="body2">
        {statement.beneficiaryName} · {t('cmp:statements.payable')}:{' '}
        <Money amountMinor={Number(statement.payable.amount)} />
      </Typography>
      <MoneyFieldRHF
        control={control}
        name="amount"
        labelKey="cmp:payouts.amount"
        helperKey="cmp:payouts.partial_help"
        required
        fullWidth
      />
      <SelectRHF
        control={control}
        name="method"
        labelKey="cmp:form.settlement_method"
        fullWidth
        options={SETTLEMENT_METHODS.map((value) => ({
          value: String(value),
          labelKey: `cmp:settlement.${String(value)}`,
        }))}
      />
      <TextFieldRHF
        control={control}
        name="reference"
        labelKey="cmp:payouts.reference"
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr' } }}
      />
    </FormDialog>
  );
}

function PayoutsTable({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const invalidate = useInvalidateSettlement();
  const payouts = useQuery({
    queryKey: [...compensationKeys.payouts(session?.tenantId), 'payouts'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/compensation/payouts', { params: { query: {} } })),
  });
  const approve = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.POST('/api/v1/compensation/payouts/{id}:approve', { params: { path: { id } } })),
    onSuccess: invalidate,
  });
  const columns: DataTableColumn<CmpPayoutView>[] = [
    {
      id: 'date',
      headerKey: 'cmp:ledger.columns.date',
      cell: (row) => <JalaliDate value={row.businessDate} />,
    },
    {
      id: 'beneficiary',
      headerKey: 'cmp:statements.columns.beneficiary',
      cell: (row) => row.beneficiaryName ?? '—',
    },
    {
      id: 'method',
      headerKey: 'cmp:form.settlement_method',
      cell: (row) => t(`cmp:settlement.${row.method}`),
    },
    {
      id: 'status',
      headerKey: 'cmp:ledger.columns.status',
      cell: (row) => (
        <StatusChip tone={payoutTone(row.status)} labelKey={`cmp:payout_status.${row.status}`} />
      ),
    },
    {
      id: 'amount',
      headerKey: 'cmp:payouts.amount',
      cell: (row) => <Money amountMinor={Number(row.amount.amount)} />,
      align: 'right',
    },
    {
      id: 'actions',
      headerKey: 'cmp:ledger.columns.actions',
      cell: (row) =>
        canManage && row.status === 'PendingApproval' ? (
          <Button
            size="small"
            onClick={() => approve.mutate(row.id)}
            disabled={approve.isPending}
            data-testid={`payout-approve-${row.id}`}
          >
            {t('cmp:payouts.approve')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Stack spacing={2}>
      {approve.error ? <ErrorState error={approve.error} compact /> : null}
      <DataTable
        captionKey="cmp:payouts.payouts_caption"
        columns={columns}
        rows={payouts.data}
        getRowId={(row) => row.id}
        isLoading={payouts.isPending}
        error={payouts.error}
        onRetry={() => void payouts.refetch()}
        emptyTitleKey="cmp:payouts.payouts_empty"
      />
    </Stack>
  );
}

const advanceSchema = z.object({
  beneficiaryId: z
    .string()
    .nullable()
    .refine((value) => Boolean(value), { error: 'common:validation.required' }),
  amount: z
    .number({ error: 'common:validation.required' })
    .int({ error: 'common:validation.money' })
    .positive({ error: 'common:validation.money' })
    .nullable()
    .refine((value) => value !== null, { error: 'common:validation.required' }),
  method: z.string(),
  reference: z.string(),
});
type AdvanceForm = z.input<typeof advanceSchema>;

function AdvancesTable({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const invalidate = useInvalidateSettlement();
  const [adding, setAdding] = useState(false);
  const advances = useQuery({
    queryKey: [...compensationKeys.payouts(session?.tenantId), 'advances'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/compensation/advances', { params: { query: {} } })),
  });
  const approve = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.POST('/api/v1/compensation/advances/{id}:approve', { params: { path: { id } } })),
    onSuccess: invalidate,
  });
  const columns: DataTableColumn<CmpAdvanceView>[] = [
    {
      id: 'date',
      headerKey: 'cmp:ledger.columns.date',
      cell: (row) => <JalaliDate value={row.businessDate} />,
    },
    {
      id: 'beneficiary',
      headerKey: 'cmp:statements.columns.beneficiary',
      cell: (row) => row.beneficiaryName ?? '—',
    },
    {
      id: 'method',
      headerKey: 'cmp:form.settlement_method',
      cell: (row) => t(`cmp:settlement.${row.method}`),
    },
    {
      id: 'status',
      headerKey: 'cmp:ledger.columns.status',
      cell: (row) => (
        <StatusChip tone={payoutTone(row.status)} labelKey={`cmp:advance_status.${row.status}`} />
      ),
    },
    {
      id: 'amount',
      headerKey: 'cmp:payouts.amount',
      cell: (row) => <Money amountMinor={Number(row.amount.amount)} />,
      align: 'right',
    },
    {
      id: 'actions',
      headerKey: 'cmp:ledger.columns.actions',
      cell: (row) =>
        canManage && row.status === 'PendingApproval' ? (
          <Button
            size="small"
            onClick={() => approve.mutate(row.id)}
            disabled={approve.isPending}
            data-testid={`advance-approve-${row.id}`}
          >
            {t('cmp:payouts.approve')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Stack spacing={2}>
      {canManage && (
        <Stack direction="row">
          <Button onClick={() => setAdding(true)} data-testid="advance-add">
            {t('cmp:payouts.add_advance')}
          </Button>
        </Stack>
      )}
      {approve.error ? <ErrorState error={approve.error} compact /> : null}
      <DataTable
        captionKey="cmp:payouts.advances_caption"
        columns={columns}
        rows={advances.data}
        getRowId={(row) => row.id}
        isLoading={advances.isPending}
        error={advances.error}
        onRetry={() => void advances.refetch()}
        emptyTitleKey="cmp:payouts.advances_empty"
      />
      {adding && <AdvanceDialog onClose={() => setAdding(false)} onDone={invalidate} />}
    </Stack>
  );
}

/** پیش‌پرداخت با نمایش سقف مجاز (AC-CMP-050.1؛ بیش از سقف ← PHX-CMP-0207). */
function AdvanceDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<AdvanceForm>({
    resolver: zodResolver(advanceSchema),
    defaultValues: { beneficiaryId: null, amount: null, method: 'Cash', reference: '' },
  });
  const beneficiaryId = useWatch({ control, name: 'beneficiaryId' });
  const limit = useQuery({
    queryKey: [...compensationKeys.payouts(session?.tenantId), 'limit', beneficiaryId],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/compensation/advances/limit', {
          params: { query: { beneficiaryId: beneficiaryId ?? '' } },
        }),
      ),
    enabled: Boolean(beneficiaryId),
  });
  const save = useMutation({
    mutationFn: (form: AdvanceForm) =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/advances', {
          body: {
            beneficiaryType: 'Provider',
            beneficiaryId: form.beneficiaryId ?? '',
            amount: { amount: form.amount ?? 0, currency: 'IRR' },
            method: form.method as CmpSettlementMethod,
            reference: form.reference.trim() || null,
            businessDate: null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['amount', 'beneficiaryId'],
        codeFields: { 'PHX-CMP-0207': 'amount' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-805-ADVANCE"
      titleKey="cmp:payouts.add_advance"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Controller
        control={control}
        name="beneficiaryId"
        render={({ field, fieldState }) => (
          <ProviderPicker
            value={field.value ?? null}
            onChange={field.onChange}
            labelKey="cmp:adjust.provider"
            testId="advance-provider"
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
          />
        )}
      />
      {limit.data && (
        <Typography variant="body2" data-testid="advance-limit">
          {t('cmp:payouts.limit', { percent: faDigits(Number(limit.data.maxPercent)) })}:{' '}
          <Money amountMinor={Number(limit.data.limit.amount)} />
        </Typography>
      )}
      <MoneyFieldRHF control={control} name="amount" labelKey="cmp:payouts.amount" required fullWidth />
      <SelectRHF
        control={control}
        name="method"
        labelKey="cmp:form.settlement_method"
        fullWidth
        options={SETTLEMENT_METHODS.map((value) => ({
          value: String(value),
          labelKey: `cmp:settlement.${String(value)}`,
        }))}
      />
      <TextFieldRHF
        control={control}
        name="reference"
        labelKey="cmp:payouts.reference"
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr' } }}
      />
    </FormDialog>
  );
}

const completeSchema = z.object({ bankReference: z.string() });
type CompleteForm = z.input<typeof completeSchema>;

function BatchesTable({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const invalidate = useInvalidateSettlement();
  const [completing, setCompleting] = useState<CmpPayoutBatchView | null>(null);
  const batches = useQuery({
    queryKey: [...compensationKeys.payouts(session?.tenantId), 'batches'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/compensation/payout-batches')),
  });
  const download = useMutation({
    mutationFn: async (batch: CmpPayoutBatchView) => {
      const response = await tenantApi.GET('/api/v1/compensation/payout-batches/{id}/file', {
        params: { path: { id: batch.id } },
        parseAs: 'blob',
      });
      if (!response.response.ok || !response.data) {
        await unwrap(Promise.resolve(response));
        return;
      }
      saveBlob(response.data as unknown as Blob, `${batch.number}.csv`);
    },
  });
  const { control, handleSubmit, formState, reset } = useForm<CompleteForm>({
    resolver: zodResolver(completeSchema),
    defaultValues: { bankReference: '' },
  });
  const complete = useMutation({
    mutationFn: (form: CompleteForm) =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/payout-batches/{id}:complete', {
          params: { path: { id: completing?.id ?? '' } },
          body: { bankReference: form.bankReference.trim() || null },
        }),
      ),
    onSuccess: () => {
      invalidate();
      reset();
      setCompleting(null);
    },
  });
  const columns: DataTableColumn<CmpPayoutBatchView>[] = [
    { id: 'number', headerKey: 'cmp:payouts.batch_number', cell: (row) => <bdi>{row.number}</bdi> },
    {
      id: 'date',
      headerKey: 'cmp:ledger.columns.date',
      cell: (row) => <JalaliDate value={row.businessDate} />,
    },
    {
      id: 'count',
      headerKey: 'cmp:payouts.batch_count',
      cell: (row) => faDigits(Number(row.count)),
      align: 'right',
    },
    {
      id: 'total',
      headerKey: 'cmp:payouts.amount',
      cell: (row) => <Money amountMinor={Number(row.total.amount)} />,
      align: 'right',
    },
    {
      id: 'status',
      headerKey: 'cmp:ledger.columns.status',
      cell: (row) => <StatusChip tone={payoutTone(row.status)} labelKey={`cmp:batch_status.${row.status}`} />,
    },
    {
      id: 'actions',
      headerKey: 'cmp:ledger.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => download.mutate(row)} data-testid={`batch-file-${row.id}`}>
            {t('cmp:payouts.download_file')}
          </Button>
          {canManage && row.status === 'Open' && (
            <Button size="small" onClick={() => setCompleting(row)} data-testid={`batch-complete-${row.id}`}>
              {t('cmp:payouts.complete')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t('cmp:payouts.file_help')}
      </Typography>
      {download.error ? <ErrorState error={download.error} compact /> : null}
      <DataTable
        captionKey="cmp:payouts.batches_caption"
        columns={columns}
        rows={batches.data}
        getRowId={(row) => row.id}
        isLoading={batches.isPending}
        error={batches.error}
        onRetry={() => void batches.refetch()}
        emptyTitleKey="cmp:payouts.batches_empty"
      />
      <FormDialog
        open={completing !== null}
        screenId="SCR-STAFF-805-COMPLETE"
        titleKey="cmp:payouts.complete_title"
        submitKey="cmp:payouts.complete"
        onSubmit={() => void handleSubmit((form) => complete.mutate(form))()}
        onClose={() => setCompleting(null)}
        isDirty={formState.isDirty}
        isSubmitting={complete.isPending}
        submitError={complete.error ?? undefined}
      >
        <TextFieldRHF
          control={control}
          name="bankReference"
          labelKey="cmp:payouts.bank_reference"
          fullWidth
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'batch-bank-reference' } }}
        />
      </FormDialog>
    </Stack>
  );
}
